/**
 * O vocabulário do Modo História que mais de uma tela usa.
 *
 * Os tipos seguem o que a API devolve (`GET /api/historia/campanhas/{slug}`),
 * e não um formato próprio de cada tela: a campanha, a partida e o editor
 * leem o mesmo capítulo.
 */

/**
 * Uma cena do capítulo, como a API a devolve.
 *
 * Mora aqui, e não na tela da campanha, porque três telas a leem: a lista de
 * capítulos, a batalha (que é onde a cena passa) e o player em si.
 */
export interface Scene {
  readonly id: number;
  readonly moment: 'abertura' | 'vitoria' | 'derrota' | 'intervalo';
  readonly ordinal: number;
  readonly kind: 'dialogo' | 'narracao' | 'imagem' | 'video';
  readonly speaker: string | null;
  readonly portrait: string | null;
  readonly portraitIndex: number;
  readonly body: string | null;
  readonly background: string | null;
  readonly bgm: string | null;
  readonly media: {
    readonly url: string;
    readonly kind: string;
    readonly mimeType: string;
    readonly width: number | null;
    readonly height: number | null;
  } | null;
}

/**
 * Esta cena toma a tela inteira, ou acontece por cima do mapa?
 *
 * Um diálogo é gente conversando no lugar em que a batalha vai acontecer — e é
 * assim nos Fire Emblem: a caixa de fala sobe por cima do tabuleiro e o jogador
 * continua vendo onde está. O que ganha a tela inteira é o que foi escolhido
 * para ganhá-la: a cena de imagem, a de vídeo, a que traz mídia enviada pelo
 * autor. Fora esses casos, tapar o mapa é esconder justamente o que a fala está
 * comentando.
 */
export function isCinematicScene(scene: Scene): boolean {
  return scene.kind === 'imagem' || scene.kind === 'video' || scene.media !== null;
}

/** Um item que o capítulo entrega na primeira vitória, no inventário da conta. */
export interface RewardItem {
  readonly contentType: 'weapons' | 'items';
  readonly contentId: number;
  readonly quantity: number;
  readonly name?: string;
}

/**
 * Um inimigo que o capítulo deixa ser convencido a mudar de lado.
 *
 * O recrutamento por conversa dos Fire Emblem, escrito no capítulo e não no
 * mapa: o mesmo mapa pode ser um capítulo em que o bandido vira aliado e outro
 * em que ele é só um bandido. A unidade é encontrada pelo NOME com que o mapa
 * a planta, que é o que o autor enxerga no editor de mapas.
 */
export interface ChapterRecruit {
  /** O nome da unidade inimiga, como o mapa a planta. */
  readonly unitName: string;
  /** Quem consegue a conversa. Vazio: qualquer um do esquadrão. */
  readonly byName?: string | undefined;
  /**
   * De qual personagem do acervo ele é.
   *
   * É por este número que o recruta entra no esquadrão do jogador depois da
   * vitória — a mesma linha de volta que os heróis da campanha usam.
   */
  readonly characterId?: number | undefined;
  /** A conversa em si, mostrada quando ele muda de lado. */
  readonly dialogue?: string | undefined;
}

/** O objetivo como o jogador lê — o mesmo vocabulário de `Objective` no motor. */
export function objectiveLabel(
  objective: { readonly kind: string; readonly turns?: number | undefined } | null | undefined,
): string {
  switch (objective?.kind) {
    case 'derrotar_todos':
      return 'derrotar todos os inimigos';
    case 'derrotar_comandante':
      return 'derrotar o comandante';
    case 'sobreviver':
      return `sobreviver ${objective.turns ?? 1} turno(s)`;
    case 'tomar_posicao':
      return 'tomar a posição marcada';
    default:
      return 'o do mapa';
  }
}
